// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://cra.link/PWA

const isLocalhost = Boolean(
    window.location.hostname === "localhost" ||
    window.location.hostname === "[::1]" ||
    window.location.hostname.match(
        /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/,
    ),
);

const UPDATE_STORAGE_KEY = "isServiceWorkerUpdated";
const UPDATE_CHECK_INTERVAL_MS = 60 * 1000;
const SERVICE_WORKER_CLEANUP_TIMEOUT_MS = 2 * 1000;
const BUILD_VERSION = import.meta.env.VITE_BUILD_VERSION;
const BASIC_AUTH_COMPATIBILITY_ENABLED =
    import.meta.env.VITE_BASIC_AUTH_COMPATIBILITY === "true";
let isReloading = false;

function reloadWhenUpdatedWorkerTakesControl() {
    // Do not reload when the service worker is installed for the first time.
    if (!navigator.serviceWorker.controller) {
        return;
    }

    navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (isReloading) {
            return;
        }

        isReloading = true;
        localStorage.removeItem(UPDATE_STORAGE_KEY);
        window.location.reload();
    });
}

export function register(config) {
    // Change: use import.meta.env instead of process.env
    if (import.meta.env.PROD) {
        if (BASIC_AUTH_COMPATIBILITY_ENABLED) {
            monitorBuildVersion(config);
            if ("serviceWorker" in navigator) {
                removeServiceWorkerFromBasicAuthEnvironment();
            }
            return;
        }

        if (!("serviceWorker" in navigator)) {
            return;
        }

        reloadWhenUpdatedWorkerTakesControl();

        // Register immediately. Waiting for window.load couples update checks to
        // every third-party resource on the page, including analytics.
        const swUrl = `/service-worker.js`;

        if (isLocalhost) {
            checkValidServiceWorker(swUrl, config);
            navigator.serviceWorker.ready.then(() => {
                console.log(
                    "This web app is being served cache-first by a service worker.",
                );
            });
        } else {
            registerValidSW(swUrl, config);
        }
    }
}

function monitorBuildVersion(config) {
    let updateNotified = false;

    const checkForBuildUpdate = async () => {
        if (document.visibilityState !== "visible" || updateNotified) {
            return;
        }

        try {
            const response = await fetch(`/version.json?t=${Date.now()}`, {
                cache: "no-store",
                credentials: "same-origin",
            });
            if (!response.ok) {
                return;
            }

            const { version } = await response.json();
            if (version && version !== BUILD_VERSION) {
                updateNotified = true;
                config?.onUpdate?.(null);
            }
        } catch (error) {
            console.error(
                "Error while checking for an application update:",
                error,
            );
        }
    };

    checkForBuildUpdate();
    document.addEventListener("visibilitychange", checkForBuildUpdate);
    window.addEventListener("online", checkForBuildUpdate);
    window.addEventListener("pageshow", checkForBuildUpdate);
    window.setInterval(checkForBuildUpdate, UPDATE_CHECK_INTERVAL_MS);
}

async function removeServiceWorkerFromBasicAuthEnvironment() {
    try {
        // Clear the cached app shell first. Firefox can leave its registration
        // API pending forever when HTTP Basic Auth is involved; waiting for it
        // here would leave the stale HTML cache untouched.
        const cacheNames = await caches.keys();
        await Promise.all(
            cacheNames.map((cacheName) => caches.delete(cacheName)),
        );

        const unregisterPromise = navigator.serviceWorker
            .getRegistrations()
            .then((registrations) =>
                Promise.allSettled(
                    registrations.map((registration) =>
                        registration.unregister(),
                    ),
                ),
            );

        // Do not let Firefox's broken registration state block application
        // startup. The promise keeps running and will still unregister the
        // workers if Firefox eventually resolves it.
        await Promise.race([
            unregisterPromise,
            new Promise((resolve) =>
                window.setTimeout(resolve, SERVICE_WORKER_CLEANUP_TIMEOUT_MS),
            ),
        ]);
    } catch (error) {
        console.error(
            "Unable to remove the service worker from the Basic Auth environment:",
            error,
        );
    }
}

function registerValidSW(swUrl, config) {
    navigator.serviceWorker
        .register(swUrl, { type: "module", updateViaCache: "none" })
        .then((registration) => {
            let notifiedWorker = null;

            const notifyInstalledWorker = (worker) => {
                if (!worker || worker === notifiedWorker) {
                    return;
                }

                notifiedWorker = worker;
                if (navigator.serviceWorker.controller) {
                    console.log("New content is available; updating.");
                    if (config && config.onUpdate) {
                        config.onUpdate(registration);
                    }
                } else {
                    console.log("Content is cached for offline use.");
                    if (config && config.onSuccess) {
                        config.onSuccess(registration);
                    }
                }
            };

            const watchInstallingWorker = () => {
                const installingWorker = registration.installing;
                if (installingWorker == null) {
                    return;
                }

                installingWorker.onstatechange = () => {
                    if (installingWorker.state === "installed") {
                        // Let the browser move the worker from `installing` to
                        // `waiting` before consumers attempt to activate it.
                        window.setTimeout(
                            () => notifyInstalledWorker(installingWorker),
                            0,
                        );
                    }
                };
            };

            registration.onupdatefound = watchInstallingWorker;

            // updatefound can fire before register() resolves. In that case the
            // new worker is already waiting and must still be acknowledged.
            if (registration.waiting) {
                notifyInstalledWorker(registration.waiting);
            } else if (registration.installing) {
                watchInstallingWorker();
            }

            const checkForUpdate = () => {
                if (document.visibilityState === "visible") {
                    registration.update().catch((error) => {
                        console.error(
                            "Error while checking for a service worker update:",
                            error,
                        );
                    });
                }
            };

            // Check now and whenever a long-lived tab becomes active again.
            checkForUpdate();
            document.addEventListener("visibilitychange", checkForUpdate);
            window.addEventListener("online", checkForUpdate);
            window.addEventListener("pageshow", checkForUpdate);
            window.setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);
        })
        .catch((error) => {
            console.error("Error during service worker registration:", error);
        });
}

function checkValidServiceWorker(swUrl, config) {
    fetch(swUrl, {
        headers: { "Service-Worker": "script" },
    })
        .then((response) => {
            const contentType = response.headers.get("content-type");
            if (
                response.status === 404 ||
                (contentType != null &&
                    contentType.indexOf("javascript") === -1)
            ) {
                navigator.serviceWorker.ready.then((registration) => {
                    registration.unregister().then(() => {
                        window.location.reload();
                    });
                });
            } else {
                registerValidSW(swUrl, config);
            }
        })
        .catch(() => {
            console.log(
                "No internet connection found. App is running in offline mode.",
            );
        });
}

export function unregister() {
    if ("serviceWorker" in navigator) {
        navigator.serviceWorker.ready
            .then((registration) => {
                registration.unregister();
            })
            .catch((error) => {
                console.error(error.message);
            });
    }
}
